/**
 * Company attendance general settings (localStorage until backend API exists).
 */

export const ATTENDANCE_GENERAL_SETTINGS_KEY =
  "hrms_attendance_general_settings_v1";
export const ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT =
  "hrms-attendance-general-settings-updated";
export const ATTENDANCE_SCHEDULE_RECALC_EVENT = "hrms-attendance-schedule-recalc";
export const ATTENDANCE_SCHEDULE_LAST_RUN_KEY =
  "hrms_attendance_schedule_last_run_ms";

export const SCHEDULE_OPTIONS = [
  { value: "realtime", label: "Realtime" },
  { value: "daily", label: "Daily (every 30 min)" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
];

/** Roles that can receive attendance schedule recalculation notifications. */
export const ATTENDANCE_NOTIFICATION_ROLES = [
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "management", label: "Management" },
  { value: "reporting_manager", label: "Reporting Manager" },
  { value: "admin", label: "Admin" },
];

export const DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW = {
  approvalMethod: "passkey",
  reminderDays: 3,
  workflowParallelApproval: false,
  workflowEscalationAfterDays: 3,
  workflowAutoApprovalIfNoAction: false,
  workflowRejectionWithReason: true,
  workflowReinitiationAllowed: true,
  workflowSteps: [{ role: "reporting_manager", slaDays: 3, notifyOnly: false }],
};

const DEFAULT_SETTINGS = {
  runAttendanceSchedule: "realtime",
  notifyRoles: ["hr", "management"],
  attendanceFinalization: { ...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW },
};

export function getDefaultAttendanceGeneralSettings() {
  return {
    ...DEFAULT_SETTINGS,
    notifyRoles: [...DEFAULT_SETTINGS.notifyRoles],
    attendanceFinalization: {
      ...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW,
      workflowSteps: [...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW.workflowSteps],
    },
  };
}

export function loadAttendanceGeneralSettings() {
  try {
    const raw = localStorage.getItem(ATTENDANCE_GENERAL_SETTINGS_KEY);
    if (!raw) return getDefaultAttendanceGeneralSettings();
    const parsed = JSON.parse(raw);
    const schedule = SCHEDULE_OPTIONS.some((o) => o.value === parsed?.runAttendanceSchedule)
      ? parsed.runAttendanceSchedule
      : DEFAULT_SETTINGS.runAttendanceSchedule;
    const notifyRoles = Array.isArray(parsed?.notifyRoles)
      ? parsed.notifyRoles.filter((r) =>
          ATTENDANCE_NOTIFICATION_ROLES.some((o) => o.value === r)
        )
      : [...DEFAULT_SETTINGS.notifyRoles];

    const finalizationRaw = parsed?.attendanceFinalization || {};
    const attendanceFinalization = {
      ...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW,
      ...finalizationRaw,
      workflowSteps:
        Array.isArray(finalizationRaw.workflowSteps) && finalizationRaw.workflowSteps.length > 0
          ? finalizationRaw.workflowSteps
          : [...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW.workflowSteps],
    };

    return { runAttendanceSchedule: schedule, notifyRoles, attendanceFinalization };
  } catch {
    return getDefaultAttendanceGeneralSettings();
  }
}

export function saveAttendanceGeneralSettings(settings) {
  const schedule = SCHEDULE_OPTIONS.some(
    (o) => o.value === settings?.runAttendanceSchedule
  )
    ? settings.runAttendanceSchedule
    : DEFAULT_SETTINGS.runAttendanceSchedule;
  const notifyRoles = Array.isArray(settings?.notifyRoles)
    ? settings.notifyRoles.filter((r) =>
        ATTENDANCE_NOTIFICATION_ROLES.some((o) => o.value === r)
      )
    : [];

  const finalizationRaw = settings?.attendanceFinalization || {};
  const attendanceFinalization = {
    ...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW,
    ...finalizationRaw,
    workflowSteps:
      Array.isArray(finalizationRaw.workflowSteps) && finalizationRaw.workflowSteps.length > 0
        ? finalizationRaw.workflowSteps
        : [...DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW.workflowSteps],
  };

  const next = { runAttendanceSchedule: schedule, notifyRoles, attendanceFinalization };
  localStorage.setItem(ATTENDANCE_GENERAL_SETTINGS_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT, { detail: next })
    );
  }
  return next;
}

export function getAttendanceFinalizationWorkflowFromSettings() {
  const settings = loadAttendanceGeneralSettings();
  return settings?.attendanceFinalization || DEFAULT_ATTENDANCE_FINALIZATION_WORKFLOW;
}

/** Milliseconds between scheduled batch recalculations (non-realtime). */
export function getScheduleIntervalMs(schedule) {
  switch (schedule) {
    case "daily":
      // Refresh status from punches/policies about every 30 minutes.
      return 30 * 60 * 1000;
    case "weekly":
      return 7 * 24 * 60 * 60 * 1000;
    case "fortnightly":
      return 14 * 24 * 60 * 60 * 1000;
    case "monthly":
      return 30 * 24 * 60 * 60 * 1000;
    case "realtime":
    default:
      return null;
  }
}

export function getScheduleLastRunMs() {
  try {
    const v = localStorage.getItem(ATTENDANCE_SCHEDULE_LAST_RUN_KEY);
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function setScheduleLastRunMs(ms = Date.now()) {
  try {
    localStorage.setItem(ATTENDANCE_SCHEDULE_LAST_RUN_KEY, String(ms));
  } catch {
    /* ignore */
  }
}

export function shouldRunScheduledRecalc(schedule, now = Date.now()) {
  const interval = getScheduleIntervalMs(schedule);
  if (!interval) return false;
  return now - getScheduleLastRunMs() >= interval;
}

export function dispatchAttendanceScheduleRecalc(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(ATTENDANCE_SCHEDULE_RECALC_EVENT, {
      detail: { batch: true, at: Date.now(), ...detail },
    })
  );
}

export function scheduleLabel(schedule) {
  return SCHEDULE_OPTIONS.find((o) => o.value === schedule)?.label || schedule;
}

/** Fallback when no employee/policy: punch-based Auto defaults to LOP. */
export function getWorkingDayDefaultStatus() {
  return "LOP";
}
